import React, { useCallback, useContext } from "react";
import CallResultFilter from "src/Components/Segments/LeadsFilterV2/CallResultFilter";
import MultiSelectFilter from "src/Components/Segments/LeadsFilterV2/MultiSelectFilter";
import { ProfileImageWithText } from "src/Components/Segments/SettingSegments/ProfileImageWithText";
import { FlexDiv } from "src/Components/UI";
import { PhoenixAppButton, PhoenixIcon } from "src/Components/UI/Phoenix";
import { DEFAULT_LEAD_SYSTEM_FILTER, LeadFilterContext } from "src/context";
import { sync } from "src/images/NewDesign";
import useLeadsFilterOptions from "src/query-hooks/useLeadsFilterOptions";
import { OptionItem } from "src/types";

export const SearchModuleFilters: React.FC<{ onResetFilter: () => void }> = ({ onResetFilter }) => {
  const {
    leadSystemFilter,
    setLeadSystemFilter,
    setLeadSystemOperators,
    leadSystemOperators,
    resetLeadSystemFilters,
    resetLeadSystemOperators,
    leadSystemFilterCount,
  } = useContext(LeadFilterContext);

  const handleReset = useCallback(() => {
    resetLeadSystemFilters();
    resetLeadSystemOperators();
    onResetFilter();
  }, [resetLeadSystemFilters, resetLeadSystemOperators]);

  const handleMultiSelectChange = useCallback(
    (field: string) => (v: any) => setLeadSystemFilter({ ...leadSystemFilter, [field]: v }),
    [leadSystemFilter, setLeadSystemFilter],
  );
  const handleOperatorChange = useCallback(
    (field: string) => (v: any) => setLeadSystemOperators({ ...leadSystemOperators, [`${field}_operator`]: v }),
    [leadSystemOperators, setLeadSystemOperators],
  );

  const { mapOptionsToKey } = useLeadsFilterOptions();

  const renderRepOption = useCallback(
    (option: OptionItem & Record<string, any>) => (
      <>
        <ProfileImageWithText
          first_name={option.first_name}
          height={24}
          last_name={option.last_name}
          profile_image={option.profile_image}
          width={24}
          textSize={8}
          hideTooltip
        />
        {option.label}
      </>
    ),
    [],
  );

  const removeFilter = useCallback(
    (filter: string) => {
      const updatedCustomFields = [...leadSystemFilter.custom_fields];
      const existingFieldIndex = updatedCustomFields.findIndex((field: any) => field.key === filter);

      if (existingFieldIndex !== -1) {
        updatedCustomFields.splice(existingFieldIndex, 1);
        setLeadSystemFilter({
          ...leadSystemFilter,
          custom_fields: updatedCustomFields,
        });
      } else {
        setLeadSystemFilter({
          ...leadSystemFilter,
          [filter]: DEFAULT_LEAD_SYSTEM_FILTER[filter],
        });
      }
    },
    [leadSystemFilter],
  );

  return (
    <FlexDiv align="center" gap={8}>
      <MultiSelectFilter
        label="Rep"
        name="reps"
        onChange={handleMultiSelectChange("reps")}
        onChangeOperator={handleOperatorChange}
        operator={leadSystemOperators.reps_operator}
        options={mapOptionsToKey("reps")}
        value={leadSystemFilter.reps}
        renderOption={renderRepOption}
        removeFilter={() => removeFilter("reps")}
      />
      <MultiSelectFilter
        label="Industry"
        name="industries"
        onChange={(v) => setLeadSystemFilter({ ...leadSystemFilter, industries: v })}
        onChangeOperator={(v) => setLeadSystemOperators({ ...leadSystemOperators, industries_operator: v })}
        operator={leadSystemOperators.industries_operator}
        options={mapOptionsToKey("industries")}
        value={leadSystemFilter.industries}
        removeFilter={() => removeFilter("industries")}
      />
      <CallResultFilter
        onChange={(v) =>
          setLeadSystemFilter({
            ...leadSystemFilter,
            call_result_filter: { ...leadSystemFilter.call_result_filter, ...v },
            history_filter: {
              ...leadSystemFilter.history_filter,
              lowerbound_date: v.lowerbound_date,
              upperbound_date: v.upperbound_date,
            },
          })
        }
        onChangeOperator={handleOperatorChange("call_result_type")}
        operator={leadSystemOperators.call_result_type_operator}
        removeFilter={() => removeFilter("call_result_filter")}
        value={leadSystemFilter.call_result_filter}
      />

      {leadSystemFilterCount ? (
        <PhoenixAppButton buttonType="ghost-small" onClick={handleReset}>
          <PhoenixIcon svg={sync} size={12} />
          Reset Filter
        </PhoenixAppButton>
      ) : null}
    </FlexDiv>
  );
};
